import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  createFeePayment,
  getFeePayments,
  deleteFeePayment,
} from "../../../services/feePaymentService";
import {
  FeePayment,
  FeePaymentCreatePayload,
  PaginatedFeePayments,
} from "../../../types/feePaymentTypes";
import { getAllPaymentMethods } from "../../../services/paymentMethodService";
import { PaymentMethodType, PaymentMethodsApiResponse } from "../../../types/paymentMethodTypes";

export const useFeePayment = () => {
  const { t } = useTranslation();
  const [feePayments, setFeePayments] = useState<PaginatedFeePayments>({
    data: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 10000,
    },
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedFeePayment, setSelectedFeePayment] =
    useState<FeePayment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodType[]>([]);

  const fetchFeePayments = useCallback(
    async (params: { [key: string]: string | number | null } = {}) => {
      setLoading(true);
      try {
        const response = await getFeePayments(params);
        setFeePayments(response.data);
        setError(null);
      } catch (err) {
        console.error(err);
        message.error(t("failed_to_fetch_fee_payments"));
        setError(t("failed_to_fetch_fee_payments"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const handleCreate = async (payload: FormData) => {
    try {
      await createFeePayment(payload as any);
      message.success(t("fee_payment_added_successfully"));
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_create_fee_payment"));
      return false;
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await getAllPaymentMethods();
      setPaymentMethods(response.data);
    } catch (error) {
      message.error(t("failed_to_fetch_payment_methods"));
      return false;
    }
  };

  const handleDelete = async (paymentId: string) => {
    try {
      await deleteFeePayment(paymentId);
      message.success(t("fee_payment_deleted_successfully"));
    } catch (error) {
      message.error(t("failed_to_delete_fee_payment"));
    }
  };

  return {
    feePayments: feePayments.data,
    infoPagination: {
      total: feePayments.pagination.total,
      page: feePayments.pagination.page,
      limit: feePayments.pagination.limit,
    },
    loading,
    error,
    selectedFeePayment,
    setSelectedFeePayment,
    isModalOpen,
    setIsModalOpen,
    fetchFeePayments,
    handleCreate,
    fetchPaymentMethods,
    paymentMethods,
    handleDelete,
  };
};

export default useFeePayment;
